import { ApiPointsAuditLog } from '@api/interfaces';
import {
  EnergyRequeueReason,
  FarmerReprocessingSource,
  ImportType,
  PointsAuditEventType,
} from '@shared/enums';
import { TFunction } from 'i18next';

/** A Record, so a new event type fails to compile until it has a label. */
const LABELS: Record<PointsAuditEventType, (t: TFunction) => string> = {
  [PointsAuditEventType.AccountsLinked]: (t) => t($ => $.pointsAudit.eventTypes.accountsLinked),
  [PointsAuditEventType.AccountsUnlinked]: (t) => t($ => $.pointsAudit.eventTypes.accountsUnlinked),
  [PointsAuditEventType.BehaviorCompleted]: (t) => (
    t($ => $.pointsAudit.eventTypes.behaviorCompleted)
  ),
  [PointsAuditEventType.BehaviorReversed]: (t) => t($ => $.pointsAudit.eventTypes.behaviorReversed),
  [PointsAuditEventType.BookingPointsReturned]: (t) => (
    t($ => $.pointsAudit.eventTypes.bookingPointsReturned)
  ),
  [PointsAuditEventType.CategoryMappingChanged]: (t) => (
    t($ => $.pointsAudit.eventTypes.categoryMappingChanged)
  ),
  [PointsAuditEventType.ExclusionChanged]: (t) => t($ => $.pointsAudit.eventTypes.exclusionChanged),
  [PointsAuditEventType.ImportCompleted]: (t) => t($ => $.pointsAudit.eventTypes.importCompleted),
  [PointsAuditEventType.ImportDiscarded]: (t) => t($ => $.pointsAudit.eventTypes.importDiscarded),
  [PointsAuditEventType.ManualPointsAdjusted]: (t) => (
    t($ => $.pointsAudit.eventTypes.manualPointsAdjusted)
  ),
  [PointsAuditEventType.ManualPointsEdited]: (t) => (
    t($ => $.pointsAudit.eventTypes.manualPointsEdited)
  ),
  [PointsAuditEventType.MembershipChanged]: (t) => (
    t($ => $.pointsAudit.eventTypes.membershipChanged)
  ),
  [PointsAuditEventType.PointsExpirationChanged]: (t) => (
    t($ => $.pointsAudit.eventTypes.pointsExpirationChanged)
  ),
  [PointsAuditEventType.PointsImported]: (t) => t($ => $.pointsAudit.eventTypes.pointsImported),
  [PointsAuditEventType.ProductExclusionChanged]: (t) => (
    t($ => $.pointsAudit.eventTypes.productExclusionChanged)
  ),
  [PointsAuditEventType.ProgramSettingsChanged]: (t) => (
    t($ => $.pointsAudit.eventTypes.programSettingsChanged)
  ),
  [PointsAuditEventType.PromotionCreated]: (t) => t($ => $.pointsAudit.eventTypes.promotionCreated),
  [PointsAuditEventType.PromotionDeleted]: (t) => t($ => $.pointsAudit.eventTypes.promotionDeleted),
  [PointsAuditEventType.PromotionEdited]: (t) => t($ => $.pointsAudit.eventTypes.promotionEdited),
  [PointsAuditEventType.RecalculationQueued]: (t) => (
    t($ => $.pointsAudit.eventTypes.recalculationQueued)
  ),
  [PointsAuditEventType.Revert]: (t) => t($ => $.pointsAudit.eventTypes.revert),
  [PointsAuditEventType.SyncCompleted]: (t) => t($ => $.pointsAudit.eventTypes.syncCompleted),
  [PointsAuditEventType.TierOverrideSet]: (t) => t($ => $.pointsAudit.eventTypes.tierOverrideSet),
};

export const eventTypeLabel = (eventType: PointsAuditEventType, t: TFunction): string => (
  LABELS[eventType]?.(t) ?? eventType
);

/** A Record, so a new import type fails to compile until it has a label. */
const IMPORT_TYPE_LABELS: Record<ImportType, (t: TFunction) => string> = {
  [ImportType.Bookings]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.importTypes.bookings)
  ),
  [ImportType.CustomerBehaviors]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.importTypes['customer behaviors'])
  ),
  [ImportType.Customers]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.importTypes.customers)
  ),
  [ImportType.ExcludedAccounts]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.importTypes['excluded accounts'])
  ),
  [ImportType.ExcludedProducts]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.importTypes['excluded products'])
  ),
  [ImportType.LinkedAccounts]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.importTypes['linked accounts'])
  ),
  [ImportType.Locations]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.importTypes.locations)
  ),
  [ImportType.Orders]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.importTypes.orders)
  ),
  [ImportType.Products]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.importTypes.products)
  ),
  [ImportType.Salespersons]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.importTypes.salespersons)
  ),
  [ImportType.ScheduledNotifications]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.importTypes['scheduled notifications'])
  ),
  [ImportType.TierOverrides]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.importTypes['tier overrides'])
  ),
  [ImportType.UpdatePoints]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.importTypes['update points'])
  ),
};

type RecalculationReason = EnergyRequeueReason | FarmerReprocessingSource;

/** A Record, so a new reprocessing source or energy requeue reason fails to compile without one. */
const REASON_LABELS: Record<RecalculationReason, (t: TFunction) => string> = {
  [EnergyRequeueReason.CategoryMappingChanged]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.reasons['category-mapping-changed'])
  ),
  [EnergyRequeueReason.MarkedReady]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.reasons['marked-ready'])
  ),
  [EnergyRequeueReason.ProgramDatesChanged]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.reasons['program-dates-changed'])
  ),
  [EnergyRequeueReason.TierRulesChanged]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.reasons['tier-rules-changed'])
  ),
  [FarmerReprocessingSource.AccountChange]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.reasons['account-change'])
  ),
  [FarmerReprocessingSource.BookingResync]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.reasons['booking-resync'])
  ),
  [FarmerReprocessingSource.Import]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.reasons.import)
  ),
  [FarmerReprocessingSource.IntegrationSync]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.reasons['integration-sync'])
  ),
  [FarmerReprocessingSource.Manual]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.reasons.manual)
  ),
  [FarmerReprocessingSource.Promotion]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.reasons.promotion)
  ),
  [FarmerReprocessingSource.Revert]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.reasons.revert)
  ),
  [FarmerReprocessingSource.SnapshotRestore]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.reasons['snapshot-restore'])
  ),
  [FarmerReprocessingSource.TierOverride]: (t) => (
    t($ => $.pointsAudit.recalculationTriggers.reasons['tier-override'])
  ),
};

/** A recalculation row's reason is a code: its reprocessing source or energy requeue reason. */
export const recalculationReasonLabel = (reason: string | null, t: TFunction): string | null => (
  reason ? REASON_LABELS[reason as RecalculationReason]?.(t) ?? reason : null
);

/**
 * Names what made a recalculation run: its import, else the change logged with it. With nothing
 * else logged, the Recalculate points button names itself, and any other run gives its reason.
 */
export const recalculationTriggerLabel = (
  row: Pick<ApiPointsAuditLog, 'reason' | 'recalculationTrigger' | 'source'>,
  t: TFunction,
): string | null => {
  if (!row.recalculationTrigger) { return null; }
  const { eventType, importType } = row.recalculationTrigger;
  if (!eventType) {
    return row.source === FarmerReprocessingSource.Manual
      ? t($ => $.pointsAudit.recalculationTriggers.recalculatePoints)
      : recalculationReasonLabel(row.reason, t);
  }
  if (eventType === PointsAuditEventType.ImportCompleted) {
    const typeLabel = importType && (IMPORT_TYPE_LABELS[importType]?.(t) ?? importType);
    return typeLabel
      ? t($ => $.pointsAudit.recalculationTriggers.importOfType, { type: typeLabel })
      : t($ => $.pointsAudit.recalculationTriggers.unknownImport);
  }
  return eventTypeLabel(eventType, t);
};
